import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageProvider";
import { PeopleTaskActionModal, type PeopleTaskPrefill } from "./PeopleTaskActionModal";
import { ExternalTaskActionModal } from "./ExternalTaskActionModal";
import type { ExternalTaskActionPrefill } from "@/lib/externalTaskExecutor";
import { loadExternalTaskLocalLocator, type ExternalTaskLocator } from "@/lib/externalTaskLocalCopy";
import { decodeGoogleTaskExternalId } from "../../../supabase/functions/_shared/googleTaskSync";

export interface ImportedTaskTarget {
  id: string;
  user_id: string;
  source?: string | null;
  external_id?: string | null;
}
interface Props {
  task: ImportedTaskTarget;
  action: "update" | "complete" | "reopen";
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

/** Imported rows never mutate their local copy from a task editor/toggle. */
export default function ImportedTaskActionModal({ task, action, open, onClose, onCreated = () => {} }: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const targetKey = [task.id,task.user_id,task.source,task.external_id,action,open].join(":");
  const [resolved,setResolved] = useState<{key:string;identity:ExternalTaskLocator|null}|null>(null);
  const [browse, setBrowse] = useState(false);
  useEffect(() => { setBrowse(false); }, [targetKey]);
  useEffect(() => {
    let active=true;
    if (open && ["microsoft_todo","apple_reminders"].includes(task.source ?? "")) {
      void loadExternalTaskLocalLocator(task.user_id,{id:task.id,source:task.source,external_id:task.external_id},()=>active).then(identity=>{
        if (active) setResolved({key:targetKey,identity});
      }).catch(()=>{if(active)setResolved({key:targetKey,identity:null});});
    }
    return ()=>{active=false;};
  },[targetKey,open,task.id,task.user_id,task.source,task.external_id]);
  const prefill = useMemo<PeopleTaskPrefill>(() => {
    const identity = task.source === "google_tasks" ? decodeGoogleTaskExternalId(task.external_id) : resolved?.key === targetKey ? resolved.identity : null;
    return {
      kind: "task", provider: task.source === "apple_reminders" ? "apple_reminders" : task.source === "microsoft_todo" ? "microsoft_todo" : "google_tasks",
      action: identity ? action : "search", action_id: crypto.randomUUID(), expected_user_id: task.user_id,
      ...(identity ? { list_id: identity.listId, item_id: identity.itemId, ...("deviceId" in identity && typeof identity.deviceId === "string" && identity.deviceId ? {device_id:identity.deviceId} : {}) } : {}),
    };
  // A new opening/local-row identity must get a distinct user action key.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, task.user_id, task.source, task.external_id, action, open, targetKey, resolved]);
  if (!open) return null;
  const supported = ["google_tasks","microsoft_todo","apple_reminders"].includes(task.source ?? "");
  if (supported && (prefill.item_id || browse)) return prefill.provider === "apple_reminders"
    ? <PeopleTaskActionModal open prefill={prefill} onClose={onClose} onCreated={onCreated} />
    : <ExternalTaskActionModal open prefill={prefill as ExternalTaskActionPrefill} onClose={onClose} onCreated={onCreated} />;
  return <Dialog.Root open onOpenChange={value => { if (!value) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70" />
      <Dialog.Content className="fixed bottom-0 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 rounded-t-3xl border border-border bg-background p-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-3xl">
        <Dialog.Title className="text-lg font-semibold">{t("people.connectedTasks")}</Dialog.Title>
        <Dialog.Description className="my-4 text-sm text-muted-foreground">{t("tasks.provider.refreshRequired")}</Dialog.Description>
        <div className="flex flex-col gap-3">
          <button className="min-h-11 rounded-xl bg-primary px-4 text-primary-foreground" onClick={() => { onClose(); navigate("/settings/connections?group=calendar"); }}>{t("people.connections")}</button>
          <button className="min-h-11 rounded-xl border border-border px-4" disabled={!supported} onClick={() => setBrowse(true)}>{t("tasks.provider.openConnected")}</button>
          <button className="min-h-11 rounded-xl px-4" onClick={onClose}>{t("people.close")}</button>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
